/* eslint-env browser */

import { getIdOrNull } from '.'
import { camelCaseToKebabCase, snakeCaseToCamelCase } from './string'

/** @enum */
export const HttpStatuses = Object.freeze({
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500
})

/** @enum */
export const ErrorMessages = Object.freeze({
  CONVENTION_ALREADY_EXISTS: 'CONVENTION_ALREADY_EXISTS',
  ID_NOT_PROVIDED: 'ID_NOT_PROVIDED',
  ACCESS_KEY_NOT_PROVIDED: 'ACCESS_KEY_NOT_PROVIDED'
})

/**
 * Authorized custom headers
 * @enum {string}
 */
export const AllowedHeaders = Object.freeze({
  SERIALIZATION_CONTEXTS: 'Serialization-Contexts',
  PORTAL_ACCESS_KEY: 'Portal-Access-Key',
  INSTITUTION_GROUP: 'InstitutionGroup',
  INSTITUTION: 'Institution'
})

export const buildUrlParameters = parameters => {
  const urlParameters = {}

  if (typeof parameters === 'object') {
    Object.keys(parameters).forEach(key => {
      urlParameters[camelCaseToKebabCase(key)] = parameters[key]
    })
  }

  return urlParameters
}

export const retrieveMetadata = meta => {
  const rawMetadata = {}

  if (typeof meta === 'object' && typeof meta.pages === 'object') {
    Object.keys(meta.pages).forEach(key => {
      if (meta.pages[key]) {
        rawMetadata[snakeCaseToCamelCase(key)] = meta.pages[key]
      }
    })
  }

  return new Metadata(rawMetadata)
}

export const getJsonBody = body => {
  return body.getReader().read().then(({ value }) => {
    const decoded = new TextDecoder('utf-8').decode(value)

    return JSON.parse(decoded)
  })
}

export class Metadata {
  constructor ({ page, first, last, next, prev, perPage, pageSize, totalPages, total } = {}) {
    this.page = page ?? 1
    this.first = first ?? 1
    this.last = last ?? 0
    this.next = next ?? 0
    this.prev = prev ?? 0
    this.perPage = perPage ?? 10
    this.pageSize = pageSize ?? perPage ?? 10
    this.totalPages = totalPages ?? 1
    this.total = total ?? 0
  }

  isSame ({ page, perPage, next }) {
    return this.page === page && this.perPage === perPage && this.next === next
  }

  toOldQueryParameters () {
    return {
      page: this.page,
      perPage: this.perPage
    }
  }

  toQueryParameters () {
    return {
      page: this.page,
      pageSize: this.pageSize
    }
  }
}

export class SerializationHeaders extends Headers {
  constructor (...serializationContexts) {
    const headers = new Headers()
    const contexts = [...serializationContexts]

    contexts.forEach(context => headers.append(AllowedHeaders.SERIALIZATION_CONTEXTS, context))

    super(headers)
  }
}

export class PortalAccessHeaders extends Headers {
  constructor({ portalAccessKey = null, institutionGroup = null, institution = null }) {
    let entityKey

    if (portalAccessKey === null) {
      throw new Error(ErrorMessages.ACCESS_KEY_NOT_PROVIDED)
    }

    if (institutionGroup !== null) {
      entityKey = 'INSTITUTION_GROUP'
    } else if (institution !== null) {
      entityKey = 'INSTITUTION'
    } else {
      throw new Error(ErrorMessages.ID_NOT_PROVIDED)
    }

    const headers = new Headers()

    const entityId = getIdOrNull(institutionGroup ?? institution)

    headers.append(AllowedHeaders.PORTAL_ACCESS_KEY, portalAccessKey)
    headers.append(AllowedHeaders[entityKey], entityId)

    super(headers)
  }
}
